const MAX_EVENT_NAME_LENGTH = 40;
const MAX_PARAM_NAME_LENGTH = 40;
const MAX_PARAM_VALUE_LENGTH = 100;
const MAX_PARAM_COUNT = 25;
const RESERVED_NAME_PREFIXES = ['firebase_', 'google_', 'ga_'];
const ITEMS_PARAM_NAME = 'items';

/**
 * Parameter, die beim Überschreiten von MAX_PARAM_COUNT erhalten bleiben müssen.
 * Firebase verwirft überzählige Parameter in unbestimmter Reihenfolge — diese
 * Liste macht die Auswahl deterministisch.
 */
const PRIORITY_PARAM_NAMES = [
    'items',
    'value',
    'currency',
    'transaction_id',
    'coupon',
    'shipping',
    'tax',
    'payment_type',
    'shipping_tier',
    'item_list_id',
    'item_list_name',
    'page_location',
    'page_title',
    'page_referrer',
    'seitentyp',
    'search_term',
    'suchbegriff'
];

/**
 * Bringt Event-Name und Parameter auf die Grenzen des Firebase-SDK. Diese sind
 * enger als bei GA4 Web: Namen nur `[A-Za-z][A-Za-z0-9_]*` und maximal 40 Zeichen,
 * String-Werte maximal 100 Zeichen, höchstens 25 Parameter pro Event.
 *
 * @param {string} eventName
 * @param {Object} params
 * @returns {{name: string, params: Object}|null} null, wenn der Event-Name unbrauchbar ist
 */
export function sanitizeEvent(eventName, params) {
    const name = sanitizeName(eventName, MAX_EVENT_NAME_LENGTH);
    if (!name) {
        return null;
    }

    return {
        name: name,
        params: sanitizeParams(params)
    };
}

/**
 * @param {Object} params
 * @returns {Object}
 */
export function sanitizeParams(params) {
    if (!params || typeof params !== 'object') {
        return {};
    }

    const sanitized = {};
    for (const [key, value] of Object.entries(params)) {
        const name = sanitizeName(key, MAX_PARAM_NAME_LENGTH);
        if (!name) {
            continue;
        }

        const sanitizedValue = sanitizeValue(name, value);
        if (sanitizedValue === undefined) {
            continue;
        }

        sanitized[name] = sanitizedValue;
    }

    return capParamCount(sanitized);
}

function sanitizeName(name, maxLength) {
    if (typeof name !== 'string') {
        return null;
    }

    const withValidChars = name.replace(/[^A-Za-z0-9_]/g, '_');
    const withLeadingLetter = withValidChars.replace(/^[^A-Za-z]+/, '');
    if (!withLeadingLetter) {
        return null;
    }
    if (hasReservedPrefix(withLeadingLetter)) {
        return null;
    }

    return withLeadingLetter.slice(0, maxLength);
}

function hasReservedPrefix(name) {
    const lowerCaseName = name.toLowerCase();
    for (const prefix of RESERVED_NAME_PREFIXES) {
        if (lowerCaseName.startsWith(prefix)) {
            return true;
        }
    }
    return false;
}

function sanitizeValue(name, value) {
    if (value === null || value === undefined) {
        return undefined;
    }

    if (name === ITEMS_PARAM_NAME) {
        return sanitizeItems(value);
    }

    switch (typeof value) {
        case 'string':
            return value.slice(0, MAX_PARAM_VALUE_LENGTH);
        case 'number':
            return Number.isFinite(value) ? value : undefined;
        case 'boolean':
            // Firebase-Parameter kennen nur String, long und double.
            return String(value);
        default:
            // Verschachtelte Objekte lassen sich über die Bridge nicht abbilden.
            return undefined;
    }
}

function sanitizeItems(items) {
    if (!Array.isArray(items)) {
        return undefined;
    }

    const sanitizedItems = [];
    for (const item of items) {
        if (!item || typeof item !== 'object') {
            continue;
        }
        sanitizedItems.push(sanitizeParams(item));
    }

    if (sanitizedItems.length === 0) {
        return undefined;
    }
    return sanitizedItems;
}

function capParamCount(params) {
    const names = Object.keys(params);
    if (names.length <= MAX_PARAM_COUNT) {
        return params;
    }

    const orderedNames = [];
    for (const name of PRIORITY_PARAM_NAMES) {
        if (names.includes(name)) {
            orderedNames.push(name);
        }
    }
    for (const name of names) {
        if (!orderedNames.includes(name)) {
            orderedNames.push(name);
        }
    }

    const capped = {};
    for (const name of orderedNames.slice(0, MAX_PARAM_COUNT)) {
        capped[name] = params[name];
    }
    return capped;
}
